import { useAtom } from "@dbeining/react-atom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Formik } from "formik";
import React from "react";
import { Alert, Modal } from "react-bootstrap";
import * as Yup from "yup";
import { userAtom } from "../../../../atoms";
import { RecurringAddTask } from "../../../../pages/Task/RecurringAddTask";
import { useTaskAddState, useTaskAddData, useTaskAddLogic, useTaskAddEmail, useTaskFormSubmission } from "../../../../hooks";
import {
  TaskBasicFields,
  TaskDateTimeFields,
  TaskAdditionalFields,
  TaskFormFooter,
} from "../";

dayjs.extend(utc);

type Props = {
    show: boolean;
    onClose: (added: boolean) => void;
    location?: string;
};

const TaskAddModel: React.FC<Props> = ({
    show,
    onClose,
    location = "",
}) => {
    const {
      alertVariant,
      setAlertVariant,
      allUsers,
      setAllUsers,
      allTaskTypes,
      setAllTaskTypes,
      visibleRecurring,
      setVisibleRecurring,
      emailTemplate,
      setEmailTemplate,
      taskTemplate,
      setTaskTemplate,
      adminEmail,
      setAdminEmail,
      taskTypeId,
      setTaskTypeId,
      taskTypeRecord,
      setTaskTypeRecord,
      allTaskTypesFull,
      setAllTaskTypesFull,
      dataReady,
      setDataReady,
    } = useTaskAddState();


    const handleClose = () => onClose(false);
    const currentUser = useAtom(userAtom);

    useTaskAddData(
      show,
      setAllUsers,
      setAllTaskTypes,
      setAdminEmail,
      setAllTaskTypesFull,
      setEmailTemplate,
      setTaskTemplate,
      setDataReady
    );

    const { getRoundedDate, calculateEndTime } = useTaskAddLogic(
      taskTypeId,
      allTaskTypesFull,
      setTaskTypeRecord
    );

    const { handleTaskSuccess } = useTaskAddEmail(
      currentUser,
      adminEmail,
      taskTemplate,
      emailTemplate
    );

    const { handleSubmit } = useTaskFormSubmission(
      visibleRecurring,
      allTaskTypes || [],
      currentUser,
      handleTaskSuccess,
      setAlertVariant,
      onClose,
      allUsers || []
    );

    if (!dataReady) {
        return <></>;
    }

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose} size={visibleRecurring ? "xl" : undefined}>
            <Formik
                validationSchema={Yup.object().shape({
                    taskTypeId: Yup.string().required("Type is required"),
                    userId: Yup.array()
                        .min(1, "Minimum of 1 user is required")
                        .required("required"),
                })}
                initialValues={{
                    taskTypeId: "",
                    userId: currentUser? [currentUser.identity.id] : [],
                    creatorId: "",
                    name: "",
                    location: location ?? "",
                    completedDate: null,
                    creationDate: dayjs(new Date()).format("YYYY-MM-DD"),
                    startTime: dayjs(getRoundedDate(30, new Date())).format("HH:mm"),
                    dueDate: dayjs(new Date()).format("YYYY-MM-DD"),
                    endTime: calculateEndTime(dayjs(getRoundedDate(30, new Date())).format("HH:mm")),
                    originalDueDate: dayjs(new Date()).format("YYYY-MM-DD"),
                    notes: "",
                    days: [],
                    recursiveInterval: undefined,
                    recursiveDuration: undefined,
                    commenceDate: undefined,
                    addToCalendar: false,
                    dayOfMonth: undefined,
                    yearlyDate: undefined
                }}
                onSubmit={(values, { setStatus, setSubmitting }) => {
                    setStatus();
                    setSubmitting(false);

                    return handleSubmit(values, setSubmitting);
                }}
            >
                {({ errors, status, touched, isSubmitting, handleSubmit, values, handleChange, setFieldValue }) => (
                    <form noValidate onSubmit={handleSubmit}>
                        <Modal.Header closeButton>
                            <Modal.Title>New Task</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="w-100">
                            <div className="d-flex">
                                <div className="w-100">
                                    <TaskBasicFields
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                        setTaskTypeId={setTaskTypeId}
                                        allTaskTypes={allTaskTypes || []}
                                        taskTypeRecord={taskTypeRecord}
                                        allUsers={allUsers || []}
                                        currentUser={currentUser}
                                    />

                                    <TaskDateTimeFields
                                        errors={errors}
                                        touched={touched}
                                        visibleRecurring={visibleRecurring}
                                    />

                                    <TaskAdditionalFields
                                        errors={errors}
                                        touched={touched}
                                        handleChange={handleChange}
                                        values={values}
                                    />
                                </div>
                                {visibleRecurring && (
                                    <>
                                        <div className="vr mx-4"></div>
                                        <RecurringAddTask
                                            values={values}
                                            errors={errors}
                                            handleChange={handleChange}
                                            touched={touched}
                                            show={true}
                                        />
                                    </>
                                )}
                            </div>
                            {status && (
                                <Alert variant={alertVariant as 'danger' | 'success' | 'warning' | 'info'} className="mt-3">
                                    {status}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <TaskFormFooter
                                isSubmitting={isSubmitting}
                                visibleRecurring={visibleRecurring}
                                setVisibleRecurring={setVisibleRecurring}
                                onClose={handleClose}
                            />
                        </Modal.Footer>
                    </form>
                )}
            </Formik>
        </Modal>
    );
};

export { TaskAddModel };
